/**
 * The presentational pieces of the "Talk to a lawyer" drawer (Consultation.js), shared with the connector
 * hand-off page (INH-5980) so a consultation asked for from Claude looks like one asked for in a chat.
 *
 * Markup, classes, copy and test ids are the drawer's, moved here unchanged. Nothing in this file reads
 * state or dispatches: the drawer and the page each decide what to submit and how.
 */
import React, { useState } from "react";
import { BadgeCheck, ChevronDown, ChevronUp, Clock, Info, Star, Zap } from "lucide-react";

import MultiAttorneyAvatars from "src/pages/chat-page/components/MultiAttorneyAvatars";
import plus100kConsultation from "src/images/plus-100k-consultation.png";
import { PLANS_V3 } from "src/utils/Constants";
import { TIER_DESCRIPTORS } from "src/domain/plan/descriptors";

/** Lawyer avatars and the heading, on the drawer's beige band. */
export const ConsultationHeader = ({ title }: { title: string }) => (
  <div className="flex-shrink-0 flex flex-col md:!flex-row md:!items-center items-start gap-2 md:!gap-[16px] px-[16px] py-[14px] md:!px-[24px] bg-[#F4F3EF]">
    <MultiAttorneyAvatars size="lg" className="!mr-0 !h-[32px] !w-[72px] md:!h-[36px] md:!w-[82px]" />
    <h2
      data-testid="consultation-title"
      className="font-['Zodiak'] font-[540] text-[20px] md:!text-[24px] leading-[130%] text-[#0F0F0F] m-0"
    >
      {title}
    </h2>
  </div>
);

const BenefitIcon = ({ children }: { children: React.ReactNode }) => (
  <div className="shrink-0 flex items-center justify-center w-[28px] h-[28px] md:!w-[32px] md:!h-[32px] rounded-[8px] bg-[#F4F3EF] mt-0.5">
    {children}
  </div>
);

/** The three value-prop rows. Each has a short mobile line and a longer desktop one. */
export const ConsultationBenefits = () => (
  <div className="flex flex-col gap-[14px] md:!gap-[28px] px-[16px] md:!px-[24px] pt-2 md:!pt-4 pb-4">
    <div className="flex items-start gap-2">
      <BenefitIcon>
        <Zap size={18} className="text-[#111111]" strokeWidth={1.5} />
      </BenefitIcon>
      <div>
        <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
          <span className="md:hidden">Fast, affordable feedback</span>
          <span className="hidden md:inline">Fast feedback and clear next steps</span>
        </p>
        <p className="font-['DM_Sans'] font-normal text-[14px] text-[#7A7A7A] m-0 mt-1 leading-[140%]">
          <span className="md:hidden">Get 30 minutes of review and 30 minutes of live feedback on your issue.</span>
          <span className="hidden md:inline">Get 1 hour of attorney time on your issue. They'll spend up to 30 minutes on preparation, then walk you through their feedback on a 30 minute call.</span>
        </p>
      </div>
    </div>

    <div className="flex items-start gap-2">
      <BenefitIcon>
        <Star size={18} className="text-[#111111]" strokeWidth={1.5} />
      </BenefitIcon>
      <div>
        <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
          <span className="md:hidden">Top-tier expertise</span>
          <span className="hidden md:inline">Work with an elite attorney</span>
        </p>
        <p className="font-['DM_Sans'] font-normal text-[14px] text-[#7A7A7A] m-0 mt-1 leading-[140%]">
          <span className="md:hidden">Elite lawyers from top law firms. Experienced with multi-state matters. <Info size={14} className="text-[#737373] inline-block align-middle" /></span>
          <span className="hidden md:inline">We'll assign you a top lawyer from our law firm, Inhouse Counsel PC. They'll serve as your family or business' general counsel.</span>
        </p>
      </div>
    </div>

    <div className="flex items-start gap-2">
      <BenefitIcon>
        <BadgeCheck size={18} className="text-[#111111]" strokeWidth={1.5} />
      </BenefitIcon>
      <div>
        <p className="font-['DM_Sans'] font-semibold text-[16px] text-[#141414] m-0 leading-[19px]">
          <span className="md:hidden">A real firm behind you</span>
          <span className="hidden md:inline">Legal work you can actually afford</span>
        </p>
        <p className="font-['DM_Sans'] font-normal text-[14px] text-[#7A7A7A] m-0 mt-1 leading-[140%]">
          <span className="md:hidden">Inhouse Counsel, PC stands behind all advice, guaranteeing its professional quality.</span>
          <span className="hidden md:inline">If you need more than a consult, your lawyer can offer flat fee discounted legal work or refer to our vetted network for a specialist.</span>
        </p>
      </div>
    </div>
  </div>
);

/** "Lawyer will respond within 2 business days", under the matter box. */
export const ResponseTimeLine = () => (
  <div className="flex items-center gap-[6px]">
    <Clock size={14} className="text-[#7A7A7A] flex-shrink-0" />
    <p className="font-['DM_Sans'] font-normal text-[14px] leading-[18px] text-[#7A7A7A] m-0">
      Lawyer will respond within <span className="font-normal text-[#197635]">2 business days</span>
    </p>
  </div>
);

/** The plan fields the proof line reads: the shape of `userMetadata`, or the hand-off page's AccountPlan. */
export interface ConsultationPlan {
  subscription_type?: string | null;
  legal_review_credits_remaining?: number | null;
  subscription_renewal_date?: string | null;
}

const isCounselPlan = (subscriptionType: string | null | undefined): boolean =>
  subscriptionType === PLANS_V3.INHOUSE_COUNSEL || subscriptionType === PLANS_V3.INHOUSE_COUNSEL_PLUS;

/**
 * Under the button: the monthly consultation count for a Counsel account, the "100K+ consultations" line for
 * every other one. `account` carries `subscription_type`, `legal_review_credits_remaining` and
 * `subscription_renewal_date` (the shape of userMetadata).
 */
export const ConsultationProof = ({ account, testId }: { account: ConsultationPlan | null | undefined; testId: string }) =>
  isCounselPlan(account?.subscription_type) ? (
    <div data-testid={testId} className="flex flex-col items-center gap-[4px]">
      <p className="font-['DM_Sans'] font-semibold text-[12px] leading-[100%] text-[#242424] m-0 text-center">
        {account?.legal_review_credits_remaining ?? 0}/{TIER_DESCRIPTORS[account?.subscription_type as keyof typeof TIER_DESCRIPTORS]?.monthlyTasks ?? 1} Monthly Consultations Available
      </p>
      {account?.subscription_renewal_date && (
        <p className="font-['DM_Sans'] font-normal text-[12px] leading-[100%] text-[#737373] m-0 text-center">
          Next Free Consultation: {new Date(account.subscription_renewal_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-[6px]">
      <img src={plus100kConsultation} alt="Attorney avatars" className="h-[24px] w-[54px]" />
      <p className="font-['DM_Sans'] font-medium text-[14px] text-[#0F0F0F] m-0 text-center leading-[150%]">100K+ Consultations delivered</p>
    </div>
  );

const FAQ_ITEMS = [
  {
    question: "What kind of advice can your attorneys provide?",
    answer: "Inhouse attorneys are generalists. They are licensed in New York, California, and Michigan, but can advise clients in other states when the issues don't turn on local law. They learn your goals, check in, and develop strategies to achieve your objectives.",
  },
  {
    question: "What if I need help with an active dispute?",
    answer: "Inhouse attorneys won't be familiar with your local court rules, so they can't research and advise on local law. They can however advise you on strategy, negotiation, and general principles of law. If you need somebody to show up in court or negotiate on your behalf, they can find one.",
  },
  {
    question: "What if I need a specialist?",
    answer: "If our Inhouse attorneys lack the expertise, we can connect you with an attorney from our network, including all 50 states and practice areas. We negotiate a discount rate and can manage the relationship so you don't have to worry about finding the right person or paying a good price.",
  },
  {
    question: "What can your attorneys do in one hour consultation?",
    answer: "Your lawyer will review the chat and take note of the documents you uploaded or generated. They have only 30 minutes to prepare for the call so they'll get through what they can if its long. On the call, they give advice for you to utilize to finish the work yourself.",
  },
  {
    question: "What are your Member Rates",
    answer: "If you need more than a consultation, your lawyer can offer a flat rate to do the work. For example, they can often send a letter from their letterhead and negotiate a settlement, draft a customer agreement from scratch, or finalize and format a financing document.",
  },
];

/** The FAQ accordion and the "Still have questions?" link. `sectionRef` lets the drawer scroll to it. */
export const ConsultationFaq = ({ sectionRef }: { sectionRef?: React.RefObject<HTMLDivElement> }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  return (
    <div ref={sectionRef} className="px-[16px] md:!px-[24px] pb-6" data-testid="consultation-faq-section">
      <div className="border-t border-[#E4E4E4]">
        {FAQ_ITEMS.map((item, idx) => (
          <div key={idx} className="border-b border-[#E4E4E4]">
            <button
              type="button"
              className="flex items-center justify-between w-full py-[16px] cursor-pointer bg-transparent border-0 text-left"
              onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
            >
              <p className="font-['DM_Sans'] font-medium text-[14px] text-[#0A0A0A] m-0 pr-4">{item.question}</p>
              {openFaqIndex === idx ? (
                <ChevronUp size={18} className="text-[#141414] shrink-0" />
              ) : (
                <ChevronDown size={18} className="text-[#141414] shrink-0" />
              )}
            </button>
            {openFaqIndex === idx && (
              <p className="font-['DM_Sans'] font-normal text-[14px] text-[#737373] m-0 pb-[16px] leading-[160%]">
                {item.answer}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-[6px] mt-[16px]">
        <Info size={14} className="text-[#6B7280] shrink-0" />
        <p className="font-['DM_Sans'] font-normal text-[13px] text-[#6B7280] m-0">
          Still have questions?{" "}
          <a href="https://www.inhouse.ai/faqs" target="_blank" rel="noopener noreferrer" className="underline text-[#6B7280] hover:text-[#141414]">
            Learn more about how we can help.
          </a>
        </p>
      </div>
    </div>
  );
};
